import type { CSSProperties } from "react"
import type { LucideIcon } from "lucide-react"
import { DollarSign, Users, Calendar, Heart, Star, Check } from "lucide-react"
import type { SectionProps } from "../types"
import { SectionContainer } from "../components/SectionContainer"
import { ColumnGrid } from "@/components/website-builder/ColumnGrid"
import type { BlockStyleOverrides } from "@/components/website-builder/blocks/types"

function resolveItemStyle(styles: BlockStyleOverrides | undefined): CSSProperties {
  if (!styles || Object.keys(styles).length === 0) return {}
  const result: CSSProperties = {}
  if (styles.background) result.background = styles.background
  if (styles.text_color) result.color = styles.text_color
  if (styles.text_align) result.textAlign = styles.text_align
  const pMap: Record<string, string> = { compact: "12px", normal: "24px", spacious: "40px" }
  if (styles.padding) result.padding = pMap[styles.padding]
  const rMap: Record<string, string> = { none: "0", sm: "4px", md: "8px", lg: "16px", full: "9999px" }
  if (styles.corner_radius) result.borderRadius = rMap[styles.corner_radius]
  return result
}

const STAT_ICONS = [DollarSign, Users, Calendar, Heart, Star, Check] as const
const ICON_MAP: Record<string, (typeof STAT_ICONS)[number]> = {
  dollar: DollarSign,
  users: Users,
  calendar: Calendar,
  heart: Heart,
  star: Star,
  check: Check,
}

const DEFAULT_STATS = [
  { label: "Total Raised", value: "$10,250" },
  { label: "Supporters", value: "127" },
  { label: "Years Active", value: "2" },
]

export function ImpactStatsSection({
  content,
  customizations: _customizations,
  styleOverrides,
}: SectionProps) {
  const heading = (content?.heading as string) ?? "Our impact"
  const columns = Math.min(4, Math.max(2, (content?.columns as number) ?? 2))
  const stats =
    (content?.stats as Array<{ label: string; value: string; icon?: string }>) ?? []

  const displayStats = stats.length >= 3 ? stats.slice(0, 3) : DEFAULT_STATS

  const statItems = displayStats.map((_stat, i) => {
    const stat = (stats.length >= 3 ? stats[i] : undefined) as { value?: string; label?: string; icon?: string } | undefined
    const iconName = stat?.icon
    const IconComponent: LucideIcon =
      (iconName ? ICON_MAP[iconName] : undefined) ?? STAT_ICONS[i % STAT_ICONS.length] ?? DollarSign
    const value = stat?.value ?? (DEFAULT_STATS[i] as { value?: string })?.value ?? "0"
    const label = stat?.label ?? (DEFAULT_STATS[i] as { label?: string })?.label ?? "Label"
    const statItemStyle = resolveItemStyle((stats[i] as Record<string, unknown> | undefined)?._styles as BlockStyleOverrides | undefined)
    return (
      <div
        key={i}
        className="flex flex-col items-center bg-background/80 px-6 py-8 text-center transition-all @sm:px-8"
        style={{
          borderRadius: "var(--nos-radius, 12px)",
          boxShadow: "var(--nos-elevation-shadow, 0 1px 3px rgb(0 0 0 / 0.08))",
          ...statItemStyle,
        }}
      >
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full @sm:h-16 @sm:w-16"
          style={{ backgroundColor: "color-mix(in srgb, var(--nos-color-primary, #3b82f6) 12%, transparent)" }}
        >
          <IconComponent
            className="h-7 w-7 @sm:h-8 @sm:w-8"
            style={{ color: "var(--nos-color-primary, #3b82f6)" }}
            strokeWidth={2}
          />
        </div>
        <p
          className="mt-4 text-3xl font-bold @sm:text-4xl @md:text-5xl"
          style={{ color: "var(--nos-color-accent, var(--nos-color-secondary, #1e40af))" }}
        >
          {value}
        </p>
        <p className="mt-1 text-sm font-medium text-muted-foreground @sm:text-base">
          {label}
        </p>
      </div>
    )
  })

  return (
    <SectionContainer
      id="impact"
      className={`bg-gradient-to-b from-muted/50 to-muted/30 ${styleOverrides?.className ?? ""}`.trim()}
      style={styleOverrides?.style}
    >
      <h2
        className="text-center text-3xl font-bold tracking-tight @sm:text-4xl"
        style={{ color: "var(--nos-heading-color, #0f172a)" }}
      >
        {heading}
      </h2>
      {columns > 1 ? (
        <div className="mt-12">
          <ColumnGrid columns={columns} itemCount={statItems.length}>
            {statItems}
          </ColumnGrid>
        </div>
      ) : (
        <div className="mt-12 grid gap-8 @sm:grid-cols-2 @md:grid-cols-3 @md:gap-10 @lg:mt-16">
          {statItems}
        </div>
      )}
    </SectionContainer>
  )
}
